import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useDashboardStore } from "@/_store/dashboard/dashboard.module.ts";
import { createWidget, type WidgetConfiguration, WidgetState } from "@/_helpers/dashboard.ts";
import { WidgetType } from "@/constants/dashboard.ts";

import SecurityAwarenessWidget from "@/components/dashboard/SecurityAwarenessWidget.vue";
import BackupAndRestoreWidget from "@/components/dashboard/BackupAndRestoreWidget.vue";
import CloudSecurityWidget from "@/components/dashboard/CloudSecurityWidget.vue";
import EmailSecurityWidget from "@/components/dashboard/EmailSecurityWidget.vue";
import EndpointSecurityWidget from "@/components/dashboard/EndpointSecurityWidget.vue";
import EdrWidget from "@/components/dashboard/EdrWidget.vue";
import UserDataGovernanceWidget from "@/components/dashboard/UserDataGovernanceWidget.vue";
import EndpointDataGovernanceWidget from "@/components/dashboard/EndpointDataGovernanceWidget.vue";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { useCloudBackupStore } from "@/_store/cloud-backup.module.ts";
import { WorkspaceType } from "@/constants/workplaces.ts";

/**
 * A composable to encapsulate all logic for creating, filtering,
 * and grouping dashboard widgets.
 */
export function useDashboardWidgets() {
  const dashboardStore = useDashboardStore();
  const storeState = storeToRefs(dashboardStore);
  const cloudBackupStore = useCloudBackupStore();
  const { enabled: cloudBackupEnabled } = storeToRefs(cloudBackupStore);

  const TICKET_WIDGET_DEFINITIONS = [
    {
      type: WidgetType.CLOUD_SECURITY,
      component: CloudSecurityWidget,
      getData: dashboardStore.fetchCloudSecurity,
    },
    {
      type: WidgetType.EMAIL_SECURITY,
      component: EmailSecurityWidget,
      getData: dashboardStore.fetchEmailSecurity,
    },
    {
      type: WidgetType.ENDPOINT_SECURITY,
      component: EndpointSecurityWidget,
      getData: dashboardStore.fetchEndpointSecurity,
    },
    { type: WidgetType.EDR, component: EdrWidget, getData: dashboardStore.fetchEdr },
    {
      type: WidgetType.USER_DATA_GOVERNANCE,
      component: UserDataGovernanceWidget,
      getData: dashboardStore.fetchUserDataGovernance,
    },
    {
      type: WidgetType.ENDPOINT_DATA_GOVERNANCE,
      component: EndpointDataGovernanceWidget,
      getData: dashboardStore.fetchEndpointDataGovernance,
    },
  ] as const;

  const allTicketsWidgets = computed(() => {
    return TICKET_WIDGET_DEFINITIONS.map((def) =>
      createWidget(def.type, def.component, storeState[def.type].value, def.getData)
    );
  });

  const securityAwarenessWidget = computed(() => {
    return createWidget(
      WidgetType.SECURITY_AWARENESS_TRAINING,
      SecurityAwarenessWidget,
      storeState[WidgetType.SECURITY_AWARENESS_TRAINING].value,
      dashboardStore.fetchSecurityAwareness
    );
  });

  const isSecurityAwarenessVisible = computed(() => {
    const { disabled, restricted } = securityAwarenessWidget.value.metadata;
    return !disabled && !restricted;
  });

  const shouldIncludeSecurityAwareness = computed(() => {
    return (
      isSecurityAwarenessVisible.value &&
      securityAwarenessWidget.value.metadata.state === WidgetState.SETUP_REQUIRED
    );
  });

  const widgetsToDisplay = computed(() => {
    const visibleTicketWidgets = allTicketsWidgets.value.filter(
      (w) => !w.metadata.disabled && !w.metadata.restricted
    );

    return shouldIncludeSecurityAwareness.value
      ? [...visibleTicketWidgets, securityAwarenessWidget.value]
      : visibleTicketWidgets;
  });

  const shouldShowSecurityAwarenessSection = computed(() => {
    return (
      isSecurityAwarenessVisible.value &&
      securityAwarenessWidget.value.metadata.state !== WidgetState.SETUP_REQUIRED
    );
  });

  const backupAndRestoreWidget = computed(() => {
    return createWidget(
      WidgetType.BACKUP_AND_RESTORE,
      BackupAndRestoreWidget,
      storeState.backupAndRestore.value,
      dashboardStore.fetchBackupAndRestore
    );
  });

  /**
   * The subscription module and the workspace type only say the section *may* appear.
   * Whether Cloud Backup is actually activated is a separate answer that lives behind
   * GET /backupAndRestore/status, so the connection state has to be fetched before the
   * section can be shown.
   */
  const isCloudBackupWidgetAvailable = computed(() => {
    const { disabled, restricted } = backupAndRestoreWidget.value.metadata;
    const { workspace } = useWorkspaceStore();
    return !disabled && !restricted && workspace.type === WorkspaceType.CHILD;
  });

  const shouldShowCloudBackupSection = computed(() => {
    return isCloudBackupWidgetAvailable.value && cloudBackupEnabled.value;
  });

  /** No-op when the section could not be shown anyway, so the request is not wasted. */
  const fetchCloudBackupConnectionState = async () => {
    if (!isCloudBackupWidgetAvailable.value) return;
    await cloudBackupStore.getConnectionState();
  };

  const widgetsByState = computed(() => {
    // Define the initial structure for the groups.
    const initialState: Record<WidgetState, WidgetConfiguration<WidgetType>[]> = {
      [WidgetState.ALL_CAUGHT_UP]: [],
      [WidgetState.SETUP_REQUIRED]: [],
      [WidgetState.HAS_OPEN_TICKETS]: [],
    };

    // Use reduce to iterate once and populate the groups.
    return widgetsToDisplay.value.reduce((groups, widget) => {
      const state = widget.metadata.state;
      // Check if the state is a valid key in our groups object
      if (state in groups) {
        groups[state].push(widget);
      }
      return groups;
    }, initialState);
  });

  const allCaughtUpWidgets = computed(() => widgetsByState.value[WidgetState.ALL_CAUGHT_UP]);
  const setupRequiredWidgets = computed(() => widgetsByState.value[WidgetState.SETUP_REQUIRED]);
  const openTicketsWidgets = computed(() => widgetsByState.value[WidgetState.HAS_OPEN_TICKETS]);

  const noAvailableWidgetsToDisplay = computed(() => widgetsToDisplay.value.length === 0);

  return {
    noAvailableWidgetsToDisplay,
    allTicketsWidgets,
    securityAwarenessWidget,
    shouldShowSecurityAwarenessSection,
    shouldShowCloudBackupSection,
    fetchCloudBackupConnectionState,
    backupAndRestoreWidget,
    openTicketsWidgets,
    setupRequiredWidgets,
    allCaughtUpWidgets,
  };
}
